import {
  parseValidData,
  detectDataInterval,
  calculateCoverage,
  calculateHourlyPattern,
  calculateWeekdayWeekendAverages,
  calculateSeasonalAverages
} from './dataAlignmentHelpers';

export interface DataStats {
  startDate: Date | null;
  endDate: Date | null;
  coverage: number;
  totalDays: number;
  missingDays: number;
  hasData: boolean;
  hourlyPattern: number[];
  hourlyStdDev: number[];
  weekdayAverage: number;
  weekendAverage: number;
  seasonalAverages: {
    winter: number;
    spring: number;
    summer: number;
    fall: number;
  };
  peakDemand?: number;
  avgDemand?: number;
  minDemand?: number;
  loadFactor?: number;
}

const EMPTY_STATS: DataStats = {
  startDate: null,
  endDate: null,
  coverage: 0,
  totalDays: 0,
  missingDays: 0,
  hasData: false,
  hourlyPattern: Array(24).fill(0),
  hourlyStdDev: Array(24).fill(0),
  weekdayAverage: 0,
  weekendAverage: 0,
  seasonalAverages: { winter: 0, spring: 0, summer: 0, fall: 0 }
};

/**
 * Calculate comprehensive data statistics
 */
export function calculateDataStats(
  data: any[],
  valueKey: string,
  fallbackKey?: string
): DataStats {
  if (!data || data.length === 0) {
    return EMPTY_STATS;
  }

  // Parse and validate data
  const validData = parseValidData(data);

  if (validData.length === 0) {
    return EMPTY_STATS;
  }

  // Detect data interval
  const dataIntervalHours = detectDataInterval(validData);

  // Calculate coverage
  const coverage = calculateCoverage(validData);

  // Calculate hourly pattern
  const { hourlyPattern, hourlyStdDev } = calculateHourlyPattern(
    validData,
    dataIntervalHours,
    valueKey,
    fallbackKey
  );

  // Calculate weekday/weekend averages
  const { weekdayAverage, weekendAverage } = calculateWeekdayWeekendAverages(
    validData,
    dataIntervalHours,
    valueKey,
    fallbackKey
  );

  // Calculate seasonal averages
  const seasonalAverages = calculateSeasonalAverages(
    validData,
    dataIntervalHours,
    valueKey,
    fallbackKey
  );

  return {
    ...coverage,
    hasData: true,
    hourlyPattern,
    hourlyStdDev,
    weekdayAverage,
    weekendAverage,
    seasonalAverages
  };
}
